/* eslint-disable @typescript-eslint/no-explicit-any */
import { z } from 'zod';
import { getTariffVoltageType, getTariffModality } from '@utils/tariff-helpers';
import { IConsumptionInfo, IPresetPayload } from '@schemas/scenarios-simulator/types';
import { ChargesCoverageTypeForManualAclPricesEnum } from '@schemas/scenarios-simulator/enums';
import { addIssue } from './add-issue';
import { validateRequiredField } from './required-field';
import { validateConsumptionMonthly } from './consumption';
import { getRequiredHistoryFields } from './tariff-helpers';
import { validateFinalEnergyPrice } from './price-validation';

// --- CONFIGURAÇÕES ---

type FieldConfig = { path: string[]; message: string };
type FieldListConfig = { path: string[]; message: string }[];

const BASE_FIELDS = new Map<string, FieldConfig>([
  ['edc', { path: ['edc'], message: 'Distribuidora é obrigatória' }],
  ['tariffGroup', { path: ['tariffGroup'], message: 'Grupo tarifário é obrigatório' }],
  ['state', { path: ['state'], message: 'Estado é obrigatório' }],
  ['icmsType', { path: ['icmsType'], message: 'Tipo de empresa é obrigatório' }],
]);

const MANAGEMENT_FIELDS = new Map<string, FieldConfig>([
  ['managementPrice', { path: ['managementPrice'], message: 'Custo mensal de gestão é obrigatório' }],
  [
    'managementTerminationFine',
    { path: ['managementTerminationFine'], message: 'Multa de rescisão de gestão é obrigatória' },
  ],
  ['migrationCost', { path: ['migrationCost'], message: 'Pacote de migração é obrigatório' }],
]);

const CLARKE_SPECIFIC_FIELDS = new Map<string, FieldConfig>([
  ['guaranteeType', { path: ['guaranteeType'], message: 'Multa de garantia de gestão é obrigatória' }],
  ['energyTerminationFine', { path: ['energyTerminationFine'], message: 'Multa de rescisão de energia é obrigatória' }],
  ['energyType', { path: ['energyType'], message: 'Tipo de energia é obrigatório' }],
  ['aux__useProductType', { path: ['aux__useProductType'], message: 'Tipo de Produto é obrigatório' }],
  ['productType', { path: ['productType'], message: 'Produto é obrigatório' }],
  ['rawEnergyPrice', { path: ['rawEnergyPrice'], message: 'Preço de energia é obrigatório' }],
  ['powerGeneratorCost', { path: ['powerGeneratorCost'], message: 'Custo de gerador é obrigatório' }],
  ['modalityType', { path: ['modalityType'], message: 'Modalidade de contratação é obrigatória' }],
  ['risk', { path: ['risk'], message: 'Cenário de risco é obrigatório' }],
  ['lowerFlexibility', { path: ['lowerFlexibility'], message: 'Flexibilidade inferior é obrigatória' }],
  ['upperFlexibility', { path: ['upperFlexibility'], message: 'Flexibilidade superior é obrigatória' }],
]);

const COMPETITOR_SPECIFIC_FIELDS = new Map<string, FieldConfig>([
  ['managerType', { path: ['managerType'], message: 'Tipo de gestor é obrigatório' }],
  ['energyType', { path: ['energyType'], message: 'Tipo de energia é obrigatório' }],
  ['guaranteeType', { path: ['guaranteeType'], message: 'Tipo de garantia é obrigatório' }],
  ['modalityType', { path: ['modalityType'], message: 'Modalidade de contratação é obrigatória' }],
  ['energyTerminationFine', { path: ['energyTerminationFine'], message: 'Multa de rescisão de energia é obrigatória' }],
]);

const VALID_BOOLEAN_VALUES = new Set(['true', 'false']);
const VALID_ENERGY_TYPE_VALUES = new Set(['INCENTIVIZED_50', 'INCENTIVIZED_100', 'CONVENTIONAL']);
const CONSUMPTION_FIELD_NAMES = new Map([
  ['showConsumption', { field: 'consumptionLowVoltage' as const }],
  ['showOffPeakConsumption', { field: 'consumptionOffPeak' as const }],
  ['showPeakConsumption', { field: 'consumptionPeak' as const }],
]);

const VOLTAGE_REQUIRED_FIELDS = new Map<string, FieldListConfig>([
  ['LOW_VOLTAGE', [{ path: ['companyType'], message: 'Tipo de empresa é obrigatório' }]],
  [
    'MEDIUM_VOLTAGE',
    [
      { path: ['lowerFlexibility'], message: 'Flexibilidade inferior é obrigatória' },
      { path: ['upperFlexibility'], message: 'Flexibilidade superior é obrigatória' },
    ],
  ],
]);

const VOLTAGE_VALIDATORS = new Map<
  string,
  (
    payload: IPresetPayload,
    basePath: Array<string | number>,
    ctx: { addIssue: (issue: z.IssueData) => void },
  ) => boolean
>([
  [
    'LOW_VOLTAGE',
    (payload, basePath, ctx) =>
      validateFieldsFromList(payload, basePath, VOLTAGE_REQUIRED_FIELDS.get('LOW_VOLTAGE') ?? [], ctx),
  ],
  [
    'MEDIUM_VOLTAGE',
    (payload, basePath, ctx) =>
      validateFieldsFromList(payload, basePath, VOLTAGE_REQUIRED_FIELDS.get('MEDIUM_VOLTAGE') ?? [], ctx),
  ],
]);

// --- HELPERS ---

const getNestedValue = (obj: any, path: string[]): any => {
  return path.reduce((current, key) => current?.[key], obj);
};

const validateFieldsFromMap = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  fieldsMap: Map<string, FieldConfig>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const results = Array.from(fieldsMap).map(([key, config]) => {
    const fieldPath = config.path;
    const value =
      fieldPath.length === 1 ? payload?.[fieldPath[0] as keyof IPresetPayload] : getNestedValue(payload, fieldPath);

    if (key === 'powerGeneratorCost') {
      const result = validatePowerGeneratorCost(value, basePath, ctx);
      return result;
    }

    if (key === 'energyType') {
      if (!VALID_ENERGY_TYPE_VALUES.has(value as string)) {
        addIssue(ctx, [...basePath, ...config.path], config.message);
        return false;
      }
      return true;
    }

    return validateRequiredField(value, [...basePath, ...config.path], config.message, ctx);
  });

  const allValid = results.every((r) => r);
  return allValid;
};

const validateFieldsFromList = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  fieldsList: FieldListConfig,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean =>
  fieldsList.every((config) =>
    validateRequiredField(
      payload?.[config.path[0] as keyof IPresetPayload],
      [...basePath, ...config.path],
      config.message,
      ctx,
    ),
  );

const runValidations = (validators: Array<() => boolean>): boolean =>
  validators.reduce((isValid, validator) => validator() && isValid, true);

const VALID_CHARGES_COVERAGE_VALUES = new Set(Object.values(ChargesCoverageTypeForManualAclPricesEnum));

const validateManualPricesCoverage = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
  message: string,
  requireManualPricesFlag = false,
): boolean => {
  const shouldValidate = !requireManualPricesFlag || payload?.aux__useManualPrices === true;
  if (!shouldValidate) return true;
  const value = payload?.energyPriceInfo?.manualAclPricesHasChargesCoverage;
  const isValid =
    typeof value === 'string' && VALID_CHARGES_COVERAGE_VALUES.has(value as ChargesCoverageTypeForManualAclPricesEnum);
  if (!isValid) addIssue(ctx, [...basePath, 'energyPriceInfo', 'manualAclPricesHasChargesCoverage'], message);
  return isValid;
};

const validateMarginFeeFields = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const validators = [
    () =>
      validateYearlyPrices(
        (payload?.marginFee?.values || undefined) as Record<string, unknown> | undefined,
        [...basePath, 'marginFee', 'values'],
        'Margem',
        ctx,
      ),
    () =>
      validateRequiredField(
        payload?.marginFee?.type,
        [...basePath, 'marginFee', 'type'],
        'Tipo de margem é obrigatório',
        ctx,
      ),
    () => {
      const isValid = VALID_BOOLEAN_VALUES.has(String(payload?.marginFee?.aux__useSamePricePerYears));
      if (!isValid) addIssue(ctx, [...basePath, 'marginFee', 'aux__useSamePricePerYears'], 'Campo é obrigatório');
      return isValid;
    },
    () => validateRequiredField(payload?.marginFee, [...basePath, 'marginFee'], 'Margem de lucro é obrigatória', ctx),
  ];
  return validators.every((validate) => validate());
};

const validateFinalEnergyPriceBlock = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  if (!payload?.finalEnergyPrice) {
    addIssue(ctx, [...basePath, 'finalEnergyPrice'], 'Preços finais de energia são obrigatórios');
    return false;
  }
  return [
    () =>
      validateFinalEnergyPrice(
        payload.finalEnergyPrice || ({} as Record<string, any>),
        [...basePath, 'finalEnergyPrice'],
        ctx,
      ),
    () =>
      validateYearlyPrices(
        (payload.finalEnergyPrice || undefined) as Record<string, unknown> | undefined,
        [...basePath, 'finalEnergyPrice'],
        'Preço final de energia',
        ctx,
      ),
  ].every((validate) => validate());
};

const validatePowerGeneratorCost = (
  cost: unknown,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  if (cost === null || cost === undefined || cost === '') return true;
  if (typeof cost !== 'number' || isNaN(cost)) {
    addIssue(ctx, [...basePath, 'powerGeneratorCost'], 'Custo de gerador deve ser um número válido');
    return false;
  }
  if (cost < 0) {
    addIssue(ctx, [...basePath, 'powerGeneratorCost'], 'Custo de gerador deve ser maior ou igual a 0');
    return false;
  }
  return true;
};

const validateBaseFields = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  return validateFieldsFromMap(payload, basePath, BASE_FIELDS, ctx);
};

const validateVoltageRequirements = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const voltageType = getTariffVoltageType(String(payload?.tariffGroup));
  if (!voltageType) return true;
  const validator = VOLTAGE_VALIDATORS.get(voltageType);
  return validator?.(payload, basePath, ctx) ?? true;
};

const validateVoltageRequirementsForAcr = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const voltageType = getTariffVoltageType(String(payload?.tariffGroup));
  if (!voltageType) return true;
  if (voltageType === 'LOW_VOLTAGE') {
    const validator = VOLTAGE_VALIDATORS.get(voltageType);
    return validator?.(payload, basePath, ctx) ?? true;
  }
  return true;
};

const validateConsumptionHistory = (
  consumptionInfo: IConsumptionInfo | Record<string, any>,
  tariffGroup: unknown,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const requiredFields = getRequiredHistoryFields(tariffGroup);

  const results = Array.from(CONSUMPTION_FIELD_NAMES).map(([requiredKey, config]) => {
    const isRequired = requiredFields[requiredKey as keyof typeof requiredFields];
    const fieldValue = consumptionInfo?.[config.field];

    if (!isRequired) {
      return true;
    }

    return validateConsumptionMonthly(fieldValue as Record<string, number>, config.field, basePath, ctx);
  });

  const allValid = results.every((r) => r);
  return allValid;
};

const validateDemandRules = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
  presetType: 'ACR' | 'CLARKE' | 'COMPETITOR',
  useSplitDemandFields?: boolean,
): boolean => {
  const tariffGroupValue = payload?.tariffGroup;
  const tariffGroupStr = String(tariffGroupValue);
  const modality = getTariffModality(tariffGroupStr);

  const validators = [];

  const validatePositiveDemand = (value: number | null | undefined, path: Array<string | number>, label: string) => {
    if (value === undefined || value === null || value === 0) {
      addIssue(ctx, path, `${label} deve ser maior que zero`);
      return false;
    }
    return true;
  };

  const validateNonNegativeDemand = (value: number | null | undefined, path: Array<string | number>, label: string) => {
    if (value === undefined || value === null) {
      addIssue(ctx, path, `${label} é obrigatório`);
      return false;
    }
    if (value < 0) {
      addIssue(ctx, path, `${label} deve ser maior ou igual a zero`);
      return false;
    }
    return true;
  };

  const validateRequiredDemand = (value: number | null | undefined, path: Array<string | number>, label: string) => {
    if (value === undefined || value === null) {
      addIssue(ctx, path, `${label} é obrigatório`);
      return false;
    }
    return true;
  };

  if (modality === 'BLUE' || modality === 'GREEN') {
    validators.push(() =>
      (useSplitDemandFields ? validateNonNegativeDemand : validatePositiveDemand)(
        useSplitDemandFields
          ? payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandWithoutDiscountOffPeak
          : payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandOffPeak,
        useSplitDemandFields
          ? [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandWithoutDiscountOffPeak']
          : [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandOffPeak'],
        useSplitDemandFields ? 'Demanda Fora de Ponta (sem desconto)' : 'Demanda Fora de Ponta',
      ),
    );
  }

  if (modality === 'BLUE') {
    validators.push(() =>
      (useSplitDemandFields ? validateNonNegativeDemand : validatePositiveDemand)(
        useSplitDemandFields
          ? payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandWithoutDiscountPeak
          : payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandPeak,
        useSplitDemandFields
          ? [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandWithoutDiscountPeak']
          : [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandPeak'],
        useSplitDemandFields ? 'Demanda Ponta (sem desconto)' : 'Demanda de Ponta',
      ),
    );
  }

  if (presetType === 'CLARKE' || modality === 'BLUE' || presetType === 'COMPETITOR') {
    const isOffPeakField = presetType === 'CLARKE';
    const validateOffPeakDemand = useSplitDemandFields ? validateNonNegativeDemand : validatePositiveDemand;
    validators.push(() =>
      validateOffPeakDemand(
        useSplitDemandFields
          ? isOffPeakField
            ? payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandWithoutDiscountOffPeak
            : payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandWithoutDiscountPeak
          : payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandOffPeak,
        useSplitDemandFields
          ? isOffPeakField
            ? [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandWithoutDiscountOffPeak']
            : [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandWithoutDiscountPeak']
          : [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandOffPeak'],
        isOffPeakField
          ? useSplitDemandFields
            ? 'Demanda Fora de Ponta (sem desconto)'
            : 'Demanda Fora de Ponta'
          : useSplitDemandFields
          ? 'Demanda Ponta (sem desconto)'
          : 'Demanda de Ponta',
      ),
    );
  }

  if (useSplitDemandFields && presetType !== 'ACR') {
    if (modality === 'BLUE' || modality === 'GREEN') {
      validators.push(() =>
        validateRequiredDemand(
          payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandWithDiscountOffPeak,
          [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandWithDiscountOffPeak'],
          'Demanda Fora de Ponta (com desconto)',
        ),
      );
    }
    if (modality === 'BLUE') {
      validators.push(() =>
        validateRequiredDemand(
          payload?.consumptionInfo?.contractedDemandInfo?.contractedDemandWithDiscountPeak,
          [...basePath, 'consumptionInfo', 'contractedDemandInfo', 'contractedDemandWithDiscountPeak'],
          'Demanda Ponta (com desconto)',
        ),
      );
    }
  }

  const result = validators.length === 0 || validators.every((validate) => validate());
  return result;
};

const validateYearlyPrices = (
  priceList: Record<string, unknown> | undefined,
  basePath: Array<string | number>,
  fieldLabel: string,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  let hasErrors = false;
  Object.entries(priceList ?? {}).forEach(([year, value]) => {
    const yearWithoutPrefix = year.startsWith('year') ? year.replace('year', '') : year;
    if (value == null) {
      addIssue(ctx, [...basePath, year], `${fieldLabel} para o ano ${yearWithoutPrefix} é obrigatório`);
      hasErrors = true;
    }
  });
  return !hasErrors;
};

const validateManagementFields = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  let hasErrors = !validateFieldsFromMap(payload, basePath, MANAGEMENT_FIELDS, ctx);

  if (payload?.managementPrice && !(payload.managementPrice >= 0)) {
    addIssue(ctx, [...basePath, 'managementPrice'], 'Custo mensal de gestão deve ser maior ou igual a 0');
    hasErrors = true;
  }

  return !hasErrors;
};

const VAREJO_EXCLUDED_FIELDS = new Set([
  'modalityType',
  'guaranteeType',
  'energyTerminationFine',
  'risk',
  'lowerFlexibility',
  'upperFlexibility',
]);

const validateClarkeSpecificFields = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const isVarejo = payload?.aux__useProductType === 'PRODUCT_TWO';
  const excluded = new Set([
    ...(isVarejo ? VAREJO_EXCLUDED_FIELDS : []),
    ...(payload?.aux__useProductType ? ['productType'] : []),
  ]);
  const fields = new Map([...CLARKE_SPECIFIC_FIELDS].filter(([k]) => !excluded.has(k)));
  return validateFieldsFromMap(payload, basePath, fields, ctx);
};

const validateClarkeMarginFee = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  return runValidations([
    () => {
      if (!VALID_ENERGY_TYPE_VALUES.has(payload?.rawEnergyPrice?.energyType as string)) {
        addIssue(ctx, [...basePath, 'rawEnergyPrice', 'energyType'], 'Tipo de energia é obrigatório');
        return false;
      }
      return true;
    },
    () =>
      validateYearlyPrices(
        (payload?.rawEnergyPrice?.rawPriceList || undefined) as Record<string, unknown> | undefined,
        [...basePath, 'rawEnergyPrice', 'rawPriceList'],
        'Preço de energia',
        ctx,
      ),
    () => validateMarginFeeFields(payload, basePath, ctx),
  ]);
};

const validateCompetitorSpecificFields = (
  payload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const result = runValidations([
    () => validateManualPricesCoverage(payload, basePath, ctx, 'Encargos são obrigatórios para ACL Concorrente'),
    () => validateFieldsFromMap(payload, basePath, COMPETITOR_SPECIFIC_FIELDS, ctx),
    () => validateFinalEnergyPriceBlock(payload, basePath, ctx),
  ]);
  return result;
};

export const validateACRPreset = (
  presetPayload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const result = runValidations([
    () => validateBaseFields(presetPayload, basePath, ctx),
    () => validateVoltageRequirementsForAcr(presetPayload, basePath, ctx),
    () =>
      validateConsumptionHistory(
        presetPayload?.consumptionInfo as Record<string, any>,
        presetPayload?.tariffGroup,
        [...basePath, 'consumptionInfo'],
        ctx,
      ),
    () => validateDemandRules(presetPayload, basePath, ctx, 'ACR'),
    () => validatePowerGeneratorCost(presetPayload?.powerGeneratorCost, basePath, ctx),
  ]);
  return result;
};

export const validateACLClarkePreset = (
  presetPayload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const isAlreadyAtAclBeforeNewRule =
    presetPayload?.isAlreadyAtAclBeforeNewRule === true ||
    String(presetPayload?.isAlreadyAtAclBeforeNewRule) === 'true';
  const useSplitDemandFields = isAlreadyAtAclBeforeNewRule && presetPayload?.energyType !== 'CONVENTIONAL';
  const isVarejo = presetPayload?.aux__useProductType === 'PRODUCT_TWO';
  const result = runValidations([
    () => validateBaseFields(presetPayload, basePath, ctx),
    () => validateVoltageRequirements(presetPayload, basePath, ctx),
    () => isVarejo || validateVoltageRequirements(presetPayload, basePath, ctx),
    () => validateDemandRules(presetPayload, basePath, ctx, 'CLARKE', useSplitDemandFields),
    () => validateManagementFields(presetPayload, basePath, ctx),
    () => validateClarkeSpecificFields(presetPayload, basePath, ctx),
    () => validateClarkeMarginFee(presetPayload, basePath, ctx),
    () =>
      validateManualPricesCoverage(presetPayload, basePath, ctx, 'Encargos são obrigatórios para preços manuais', true),
    () =>
      validateConsumptionHistory(
        presetPayload?.consumptionInfo as IConsumptionInfo,
        presetPayload?.tariffGroup,
        [...basePath, 'consumptionInfo'],
        ctx,
      ),
  ]);
  return result;
};

export const validateACLCompetitorPreset = (
  presetPayload: IPresetPayload,
  basePath: Array<string | number>,
  ctx: { addIssue: (issue: z.IssueData) => void },
): boolean => {
  const isAlreadyAtAclBeforeNewRule =
    presetPayload?.isAlreadyAtAclBeforeNewRule === true ||
    String(presetPayload?.isAlreadyAtAclBeforeNewRule) === 'true';
  const useSplitDemandFields = isAlreadyAtAclBeforeNewRule && presetPayload?.energyType !== 'CONVENTIONAL';
  const result = runValidations([
    () => validateCompetitorSpecificFields(presetPayload, basePath, ctx),
    () => validateBaseFields(presetPayload, basePath, ctx),
    () => validateVoltageRequirements(presetPayload, basePath, ctx),
    () => validateDemandRules(presetPayload, basePath, ctx, 'COMPETITOR', useSplitDemandFields),
    () => validateManagementFields(presetPayload, basePath, ctx),
    () => validatePowerGeneratorCost(presetPayload?.powerGeneratorCost, basePath, ctx),
    () =>
      validateConsumptionHistory(
        presetPayload?.consumptionInfo as IConsumptionInfo,
        presetPayload?.tariffGroup,
        [...basePath, 'consumptionInfo'],
        ctx,
      ),
  ]);
  return result;
};
